import { IDashboardStats } from "../../models/DashboardStats";
import PriceFormat from "../../utils/priceFormat";
import DashboardTile from "./DashboardTile";

interface IFinancialInformationTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type DashboardStatsExtended = IDashboardStats & {
  vehiclesAwaitingPaymentCount?: number;
};

type RowConfig = {
  label: string;
  countKey?: keyof DashboardStatsExtended;
  valueKey?: keyof DashboardStatsExtended;
  valueFormat?: "price";
  bgClass: string;
  textClass: string;
};

const rowConfig: RowConfig[] = [
  {
    label: "Vehicles Awaiting Payment",
    countKey: "vehiclesAwaitingPayment",
    valueKey: "vehiclesAwaitingPaymentValue",
    valueFormat: "price",
    bgClass: "bg-blue-50",
    textClass: "text-blue-700",
  },
  {
    label: "Vehicle Purchased MTD",
    countKey: "vehiclePurchasedMTD",
    valueKey: "vehiclePurchasedMTDValue",
    valueFormat: "price",
    bgClass: "bg-emerald-50",
    textClass: "text-emerald-700",
  },
  {
    label: "Vehicles Awaiting Purchase",
    countKey: "vehiclesAwaitingPurchase",
    valueKey: "vehiclesAwaitingPurchaseValue",
    valueFormat: "price",
    bgClass: "bg-amber-50",
    textClass: "text-amber-700",
  },
  {
    label: "Parts Sold Total",
    countKey: "partsSoldTotal",
    valueKey: "partsSoldTotalValue",
    valueFormat: "price",
    bgClass: "bg-orange-50",
    textClass: "text-orange-700",
  },
  {
    label: "Unlisted Stock",
    countKey: "unlistedStockCount",
    valueKey: "unlistedStockValue",
    valueFormat: "price",
    bgClass: "bg-teal-50",
    textClass: "text-teal-700",
  },
  {
    label: "Listed Stock",
    countKey: "listedStockCount",
    valueKey: "listedStockValue",
    valueFormat: "price",
    bgClass: "bg-violet-50",
    textClass: "text-violet-700",
  },
];

function formatCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

function formatValue(value: number | null | undefined, asPrice: boolean): string {
  if (value == null || Number.isNaN(value)) return "—";
  if (asPrice) return PriceFormat(value);
  return String(value);
}

export default function FinancialInformationTable({ dashboardStats, span = 2 }: IFinancialInformationTableProps) {
  if (!dashboardStats) return null;

  const stats = dashboardStats as DashboardStatsExtended;

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">Financial Information</h3>
          <p className="text-sm text-gray-500 mt-0.5">Count and value by metric over the last year</p>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-base">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">Metric</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">Count</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">Value</th>
              </tr>
            </thead>
            <tbody>
              {rowConfig.map((row) => {
                let count: number | null | undefined =
                  row.countKey != null
                    ? (stats[row.countKey] as number | null | undefined)
                    : undefined;
                let value: number | null | undefined =
                  row.valueKey != null
                    ? (stats[row.valueKey] as number | null | undefined)
                    : undefined;

                const showCount = row.countKey != null;
                const showValue = row.valueKey != null;

                return (
                  <tr key={row.label} className={`font-medium transition-colors ${row.bgClass}`}>
                    <td className={`py-2.5 pl-4 pr-4 ${row.textClass}`}>{row.label}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${row.textClass}`}>
                      {showCount ? formatCount(count) : "—"}
                    </td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${row.textClass}`}>
                      {showValue ? formatValue(value, row.valueFormat === "price") : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
